// src/pages/projects/ProjectCreateEdit.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import useProjectStore from '../../stores/projectStore';
import useProjectTemplateStore from '../../stores/projectTemplateStore';
import ProjectForm from '../../components/projects/ProjectForm';
import ProjectCreationOptions from '../../components/projects/ProjectCreationOptions';

const ProjectCreateEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  
  // Try to get context from parent ProjectContainer (when in edit mode via nested route)
  let contextData = null;
  try {
    contextData = useOutletContext();
  } catch (e) {
    // Not nested, that's okay
  }
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showCreationOptions, setShowCreationOptions] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [duplicateProject, setDuplicateProject] = useState(null);
  
  const { 
    currentProject, 
    fetchProject, 
    createProject,
    updateProject,
    isLoading,
    error: storeError,
    clearError
  } = useProjectStore();

  const {
    createProjectFromTemplate,
    duplicateProject: duplicateProjectAction
  } = useProjectTemplateStore();
  
  const isEditMode = !!id;
  const templateId = searchParams.get('templateId');
  const duplicateId = searchParams.get('duplicateId');
  
  // Check if we're nested under ProjectContainer
  const isNestedRoute = contextData && contextData.project;
  
  // Load project data if in edit mode and NOT nested (nested routes get data from parent)
  useEffect(() => {
    if (isEditMode && !isNestedRoute) {
      fetchProject(id).catch(err => {
        setError('Failed to load project. Please try again.');
      });
    } else if (!isEditMode) {
      // Clear current project when creating a new one
      clearError();
      // Show creation options if no template or duplicate specified
      if (!templateId && !duplicateId) {
        setShowCreationOptions(true);
      }
    }
  }, [isEditMode, id, templateId, duplicateId, fetchProject, clearError, isNestedRoute]);

  // Handle form submission
  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      let newProject;
      
      if (isEditMode) {
        await updateProject(id, formData);
        navigate(`/projects/${id}`);
        return;
      }
      
      // Handle different creation modes
      if (selectedTemplate) {
        newProject = await createProjectFromTemplate(selectedTemplate._id, formData);
        enqueueSnackbar('Project created from template successfully', { variant: 'success' });
      } else if (duplicateProject) {
        newProject = await duplicateProjectAction(duplicateProject._id, formData);
        enqueueSnackbar('Project duplicated successfully', { variant: 'success' });
      } else {
        newProject = await createProject(formData);
        enqueueSnackbar('Project created successfully', { variant: 'success' });
      }
      
      navigate(`/projects/${newProject._id}`);
    } catch (err) {
      setError(err.message || 'An error occurred while saving the project.');
      enqueueSnackbar('Failed to create project', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle cancel  
  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/projects/${id}`);
    } else {
      navigate('/projects');
    }
  };

  // Handle creation option selection
  const handleCreateFromScratch = () => {
    setShowCreationOptions(false);
    setSelectedTemplate(null);
    setDuplicateProject(null);
  };

  const handleCreateFromTemplate = (template) => {
    setSelectedTemplate(template);
    setShowCreationOptions(false);
    setDuplicateProject(null);
  };

  const handleDuplicateProject = (project) => {
    setDuplicateProject(project);
    setShowCreationOptions(false);
    setSelectedTemplate(null);
  };
  
  // Get page title based on creation mode
  const getPageTitle = () => {
    if (isEditMode) return 'Edit Project';
    if (selectedTemplate) return `Create Project from "${selectedTemplate.name}" Template`;
    if (duplicateProject) return `Duplicate "${duplicateProject.name}" Project`;
    return 'Create New Project';
  };
  
  // Get the project to use (from context if nested, from store otherwise)
  const projectToUse = isNestedRoute ? contextData.project : currentProject;

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link color="inherit" href="/projects">
          Projects
        </Link>
        {isEditMode && projectToUse && (
          <Link color="inherit" href={`/projects/${id}`}>
            {projectToUse.name}
          </Link>
        )}
        <Typography color="text.primary">
          {isEditMode ? 'Edit Project' : 'Create Project'}
        </Typography>
      </Breadcrumbs>
      
      {/* Page Title */}
      <Typography variant="h4" sx={{ mb: 4 }}>
        {getPageTitle()}
      </Typography>
      
      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Error Message */}
      {(error || storeError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error || storeError}
        </Alert>
      )}

      {/* Project Creation Options Dialog */}
      <ProjectCreationOptions
        open={showCreationOptions}
        onClose={() => setShowCreationOptions(false)}
        onCreateFromScratch={handleCreateFromScratch}
        onCreateFromTemplate={handleCreateFromTemplate}
        onDuplicateProject={handleDuplicateProject}
      />
      
      {/* Form */}
      {(!isEditMode || (isEditMode && projectToUse)) && !showCreationOptions && (
        <ProjectForm 
          project={isEditMode ? projectToUse : null}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
          selectedTemplate={selectedTemplate}
          duplicateProject={duplicateProject}
        />
      )}
    </Box>
  );
};

export default ProjectCreateEdit;